import { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import withLocalization from '~/hoc/withLocalization';
import LoadingSpinner from '~/elements/LoadingSpinner';
import Select from '~/elements/Select';
import { APP_TO_MOMENT_LOCALE } from '~/utils/constants';
import { generateYearOptions } from '~/utils/timeCalc';

import './HolidayView.css';

const VIEW_CARD = 'card';
const VIEW_LIST = 'list';

class HolidayView extends Component {
    constructor(props) {
        super(props);
        const currentYear = new Date().getFullYear();
        this.state = {
            year: currentYear,
            view: VIEW_CARD,
            holidays: [],
            loading: false,
        };
    }

    componentDidMount() {
        this.loadHolidays();
    }

    // Returns the correct moment locale for the currently selected app language.
    // Reading directly from commonStore (not the global moment state) prevents
    // stale-locale issues when the stored session locale differs from expectation.
    getMomentLocale() {
        const { commonStore } = this.props;
        return APP_TO_MOMENT_LOCALE[commonStore.locale] || 'en';
    }

    // Convenience: moment instance with the correct locale applied
    mDate(isoDate) {
        return moment(isoDate).locale(this.getMomentLocale());
    }

    loadHolidays = async () => {
        const { clientStore, commonStore, t } = this.props;
        this.setState({ loading: true });
        try {
            const response = await clientStore.getActiveHolidays({ year: this.state.year });
            this.setState({ holidays: response?.holidays || [] });
        } catch {
            commonStore.addNotification(t('Failed to load holidays'), null, 'error');
        } finally {
            this.setState({ loading: false });
        }
    };

    handleYearChange = (opt) => {
        this.setState({ year: opt.value }, this.loadHolidays);
    };

    groupByMonth = () => {
        const byMonth = Array.from({ length: 12 }, () => []);
        this.state.holidays.forEach(h => {
            const m = moment(h.iso_date).month();
            if (m >= 0 && m < 12) byMonth[m].push(h);
        });
        return byMonth;
    };

    renderCardView() {
        const { t } = this.props;
        const { year } = this.state;
        const locale = this.getMomentLocale();
        const byMonth = this.groupByMonth();

        return (
            <div className="hv-card-grid">
                {byMonth.map((monthHolidays, idx) => (
                    <div key={idx} className="hv-month-card">
                        <div className="hv-month-header">
                            {/* Localized month abbreviation — consistent with day names below */}
                            {moment().locale(locale).month(idx).format('MMM').toUpperCase()} {year}
                        </div>
                        <div className="hv-month-body">
                            {monthHolidays.length === 0 ? (
                                <div className="hv-no-holidays">{t('No Holidays')}</div>
                            ) : (
                                monthHolidays.map((h, i) => (
                                    <div key={i} className="hv-item">
                                        <div className="hv-date-col">
                                            <span className="hv-day-num">{moment(h.iso_date).format('DD')}</span>
                                            <span className="hv-day-name">{this.mDate(h.iso_date).format('ddd')}</span>
                                        </div>
                                        <div className="hv-name">{h.custom_name || h.name}</div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    renderListView() {
        const { t } = this.props;
        const { holidays } = this.state;

        return (
            <div className="hv-list-wrap">
                <table className="table hv-table">
                    <thead>
                        <tr>
                            <th>{t('Date')}</th>
                            <th>{t('Day')}</th>
                            <th>{t('Holiday Name')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {holidays.length === 0 ? (
                            <tr>
                                <td colSpan="3" className="hv-empty-row">{t('No Holidays')}</td>
                            </tr>
                        ) : (
                            holidays.map((h, i) => (
                                <tr key={i}>
                                    <td>{this.mDate(h.iso_date).format('DD MMM YYYY')}</td>
                                    <td>{this.mDate(h.iso_date).format('dddd')}</td>
                                    <td>{h.custom_name || h.name}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        );
    }

    render() {
        const { t } = this.props;
        const { year, view, loading } = this.state;
        const currentYear = new Date().getFullYear();
        const yearOptions = generateYearOptions().map(y => ({ value: Number(y), label: y }));

        return (
            <div className="hv-root">
                <div className="hv-toolbar">
                    <div className="hv-year-select">
                        <Select
                            options={yearOptions}
                            value={yearOptions.find(y => y.value === year) || null}
                            onChange={this.handleYearChange}
                        />
                    </div>
                    <div className="hv-toggle">
                        <button
                            className={`hv-toggle-btn${view === VIEW_CARD ? ' active' : ''}`}
                            onClick={() => this.setState({ view: VIEW_CARD })}
                            title={t('Card View')}
                        >
                            <i className="fa fa-th-large" />
                        </button>
                        <button
                            className={`hv-toggle-btn${view === VIEW_LIST ? ' active' : ''}`}
                            onClick={() => this.setState({ view: VIEW_LIST })}
                            title={t('List View')}
                        >
                            <i className="fa fa-list" />
                        </button>
                    </div>
                </div>
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    view === VIEW_CARD ? this.renderCardView() : this.renderListView()
                )}
            </div>
        );
    }
}

export default inject('clientStore', 'commonStore')(observer(withLocalization(HolidayView)));
